// Figma Design Tokens for DigiSlides
// Colors extracted from Figma designs
// Fonts: Bricolage Grotesque (Headings), Outfit (Body/UI), Inter (Fallback)

// ============================================
// PRIMARY COLOR SCALE (Red Brand Colors)
// ============================================
export const primaryScale = {
  0: '#020001',
  5: '#110304',
  10: '#200508',
  15: '#2F080B',
  20: '#3F0A0F',
  25: '#4E0C12',
  30: '#5D0F16',
  35: '#6C111A',
  40: '#7B131D',
  45: '#8A1621',
  50: '#991824',
  55: '#A81B28',
  60: '#B71D2B',
  65: '#C61F2F', // Primary brand color
  70: '#D52232',
  75: '#E42436',
  80: '#E63343',
  85: '#E84151',
  90: '#E94F5E',
  95: '#EB5E6B',
  100: '#ED6C78',
  105: '#EF7B86',
  110: '#F18993',
  115: '#F298A0',
  120: '#F4A6AD',
  125: '#F6B5BB',
  130: '#F8C3C8',
  135: '#F9D1D5',
  140: '#FBE0E3',
  145: '#FDEEF0',
  150: '#FFFDFD',
};

// ============================================
// NATURAL/NEUTRAL COLOR SCALE (Grays)
// ============================================
export const naturalScale = {
  0: '#000001',
  5: '#050507',
  10: '#09090E',
  15: '#0E0E14',
  20: '#12121B',
  25: '#171622',
  30: '#1C1B28',
  35: '#201F2F',
  40: '#272636',
  45: '#2E2D3C',
  50: '#353543',
  55: '#3D3C4A',
  60: '#444350',
  65: '#4B4A57', // Body text
  70: '#52515E',
  75: '#595864',
  80: '#605F6B',
  85: '#676771',
  90: '#6E6E78',
  95: '#76757F',
  100: '#7D7C85',
  105: '#84838C',
  110: '#8B8A93',
  115: '#929299',
  120: '#9999A0',
  125: '#A0A0A7',
  130: '#A7A7AD',
  135: '#AEAEB4',
  145: '#BDBCC1',
  124: '#CBCBCE',
  128: '#D2D2D5',
  132: '#D9D9DC',
  136: '#E0E0E2',
  144: '#EFEFF0',
  148: '#F6F6F6',
  150: '#FDFDFD',
};

// ============================================
// SECONDARY COLOR SCALE (Purple)
// ============================================
export const secondaryScale = {
  0: '#010002',
  5: '#090311',
  10: '#100520',
  15: '#18082F',
  20: '#1F0A3F',
  25: '#270C4E',
  30: '#2E0F5D',
  35: '#36116C',
  40: '#3D137B',
  45: '#45168A',
  50: '#4C1899',
  55: '#541BA8',
  60: '#5B1DB7',
  65: '#631FC6',
  70: '#6B22D5',
  75: '#7224E4',
  80: '#7B33E6',
  85: '#8541E8',
  90: '#8E4FE9',
  95: '#975EEB',
  100: '#A16CED',
  105: '#AA7BEF',
  110: '#B389F1',
  115: '#BC98F2',
  120: '#C6A6F4',
  125: '#CFB5F6',
  130: '#D8C3F8',
  135: '#E2D1F9',
  140: '#EBE0FB',
  145: '#F4EEFD',
  150: '#FEFDFF',
};

// ============================================
// SIMPLIFIED COLOR PALETTE (For easy use)
// ============================================
export const colors = {
  // Primary Brand Colors
  primary: {
    main: primaryScale[65], // '#C61F2F'
    light: primaryScale[85], // '#E84151'
    dark: primaryScale[55], // '#A81B28'
    lighter: primaryScale[100], // '#ED6C78'
    darker: primaryScale[45], // '#8A1621'
    contrastText: '#FFFFFF',
    // Full scale access
    scale: primaryScale,
  },
  
  // Secondary Colors (Purple)
  secondary: {
    main: secondaryScale[60], // '#5B1DB7'
    light: secondaryScale[80], // '#7B33E6'
    dark: secondaryScale[50], // '#4C1899'
    lighter: secondaryScale[100], // '#A16CED'
    darker: secondaryScale[40], // '#3D137B'
    contrastText: '#FFFFFF',
    // Full scale access
    scale: secondaryScale,
  },
  
  // Neutral Colors
  neutral: {
    white: '#FFFFFF',
    black: naturalScale[0],
    // Aliases for common use
    gray50: naturalScale[148],
    gray100: naturalScale[144],
    gray200: naturalScale[136],
    gray300: naturalScale[128],
    gray400: naturalScale[120],
    gray500: naturalScale[100],
    gray600: naturalScale[80],
    gray700: naturalScale[65], // Body text
    gray800: naturalScale[40],
    gray900: naturalScale[20],
    // Full scale access
    scale: naturalScale,
  },
  
  // Status Colors
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
    bg: '#D1FAE5',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
    bg: '#FEE2E2',
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
    bg: '#FEF3C7',
  },
  info: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#2563EB',
    bg: '#DBEAFE',
  },
  
  // Background Colors
  background: {
    default: naturalScale[148], // '#F6F6F6'
    paper: '#FFFFFF',
    dark: naturalScale[20], // '#12121B'
    darkPaper: naturalScale[30], // '#1C1B28'
  },
  
  // Text Colors
  text: {
    primary: naturalScale[65], // '#4B4A57'
    secondary: naturalScale[95], // '#76757F'
    disabled: naturalScale[125], // '#A0A0A7'
    hint: naturalScale[135], // '#AEAEB4'
  },
};

// ============================================
// TYPOGRAPHY SCALE
// ============================================
export const fontSizes = {
  '2xs': '10.24px',
  xs: '12.80px',
  base: '16px',
  sm: '20px',
  md: '25px',
  lg: '31.25px',
  xl: '39.06px',
  '2xl': '48.83px',
  '3xl': '61.04px',
  '4xl': '76.29px',
  '5xl': '95.37px',
  '6xl': '119.21px',
  '7xl': '149.01px',
  '8xl': '186.26px',
};

export const fontWeights = {
  thin: 100,
  extraLight: 200,
  light: 300,
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
  extraBold: 800,
  black: 900,
};

export const lineHeights = {
  // Body line heights
  bodySmTight: 1.4, // 140%
  bodySmNormal: 1.5, // 150%
  bodySmLoose: 1.6, // 160%
  bodyMdTight: 1.4, // 140%
  bodyMdNormal: 1.5, // 150%
  bodyMdLoose: 1.6, // 160%
  // Heading line heights
  headingSmTight: 1.2, // 120%
  headingSmNormal: 1.3, // 130%
  headingMdTight: 1.2, // 120%
  headingMdNormal: 1.3, // 130%
  // Display line heights
  displayTight: 1.1, // 110%
  displayNormal: 1.2, // 120%
};

export const typography = {
  // Font Families
  fontFamily: '"Outfit", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  fontFamilyHeading: '"Bricolage Grotesque", "Outfit", "Inter", sans-serif',
  
  // Font Weights (aliases)
  fontWeightThin: fontWeights.thin,
  fontWeightLight: fontWeights.light,
  fontWeightRegular: fontWeights.regular,
  fontWeightMedium: fontWeights.medium,
  fontWeightSemiBold: fontWeights.semiBold,
  fontWeightBold: fontWeights.bold,
  fontWeightExtraBold: fontWeights.extraBold,
  fontWeightBlack: fontWeights.black,
  
  // Typography Variants
  h1: {
    fontFamily: '"Bricolage Grotesque", "Outfit", "Inter", sans-serif',
    fontSize: fontSizes['4xl'], // 76.29px
    fontWeight: fontWeights.extraBold,
    lineHeight: lineHeights.displayNormal,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontFamily: '"Bricolage Grotesque", "Outfit", "Inter", sans-serif',
    fontSize: fontSizes['3xl'], // 61.04px
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.headingMdNormal,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontFamily: '"Bricolage Grotesque", "Outfit", "Inter", sans-serif',
    fontSize: fontSizes['2xl'], // 48.83px
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.headingMdTight,
  },
  h4: {
    fontFamily: '"Bricolage Grotesque", "Outfit", "Inter", sans-serif',
    fontSize: fontSizes.xl, // 39.06px
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.headingSmNormal,
  },
  h5: {
    fontFamily: '"Bricolage Grotesque", "Outfit", "Inter", sans-serif',
    fontSize: fontSizes.lg, // 31.25px
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.headingSmNormal,
  },
  h6: {
    fontFamily: '"Bricolage Grotesque", "Outfit", "Inter", sans-serif',
    fontSize: fontSizes.md, // 25px
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.headingSmNormal,
  },
  subtitle1: {
    fontSize: fontSizes.sm, // 20px
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.bodyMdNormal,
  },
  subtitle2: {
    fontSize: fontSizes.base, // 16px
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.bodySmNormal,
  },
  body1: {
    fontSize: fontSizes.base, // 16px
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.bodyMdNormal,
  },
  body2: {
    fontSize: fontSizes.xs, // 12.80px
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.bodySmNormal,
  },
  button: {
    fontSize: fontSizes.base, // 16px
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.bodyMdNormal,
    textTransform: 'none',
  },
  caption: {
    fontSize: fontSizes.xs, // 12.80px
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.bodySmLoose,
  },
  overline: {
    fontSize: fontSizes['2xs'], // 10.24px
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.bodySmLoose,
    textTransform: 'uppercase',
  },
};

// ============================================
// SPACING SCALE (in pixels)
// ============================================
export const spacing = {
  0: 0,
  0.5: 2,
  1: 4,
  1.5: 6,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
  24: 96,
  32: 128,
  40: 160,
  48: 192,
  56: 224,
  64: 256,
  80: 320,
  96: 384,
  120: 480,
  140: 560,
  160: 640,
  180: 720,
  192: 768,
  256: 1024,
  320: 1280,
  360: 1440,
  400: 1600,
  480: 1920,
  // Aliases for common use
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
};

// ============================================
// BORDER RADIUS SCALE (in pixels)
// ============================================
export const borderRadius = {
  none: 0,
  xxxs: 1,
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xlIncreased: 28,
  xxl: 48,
  full: 999,
};

// ============================================
// SHADOWS
// ============================================
export const shadows = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  xxl: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  // Focus shadows
  focus: `0 0 0 4px rgba(${naturalScale[5]}, 0.2)`,
  primaryFocus: `0 0 0 3px rgba(${primaryScale[65]}, 0.2), 0 0 0 6px rgba(${primaryScale[70]}, 0.1)`,
};

// ============================================
// Z-INDEX SCALE
// ============================================
export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  fixed: 1200,
  modalBackdrop: 1300,
  modal: 1400,
  popover: 1500,
  tooltip: 1600,
  // MUI compatibility
  appBar: 1100,
  drawer: 1200,
  snackbar: 1400,
};

// ============================================
// BREAKPOINTS (for responsive design)
// ============================================
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};
